// 「最近刪除的歌」（後台 cf-worker/songTrash.mjs，migration 0050）：
//   刪一首歌不是真的刪，進「最近刪除的歌」留 30 天（cue、留言、影片、音樂都還在）；這段時間製作可以救回來，歌單順序回到原本的位置。
//   跟影片版本的「最近刪除」（storageApi.ts）同一套做法。只有製作看得到、救得回來（後台才是真正的權限）。
// 這個檔案：呼叫後台 + 畫面上要寫的字（純函式，songTrash.test.ts 有測）。

import { apiBase, getSessionToken } from './api';
import { formatDateTime } from './serverTime';

const enc = encodeURIComponent;

/** 刪掉的歌留幾天（後台 SONG_KEEP_DAYS） */
export const SONG_KEEP_DAYS = 30;

/** 最近刪除的一首歌 */
export interface DeletedSong {
  id: string;
  name: string;
  code: string | null;
  order: number;
  status: string;
  deletedAt: string;
  deletedBy: { id: string; name: string | null } | null;
  purgeAfter: string | null;
  /** 還剩幾天就清掉（0 = 這幾分鐘就會清） */
  daysLeft: number;
  /** 裡面有什麼（救回來會回來多少東西） */
  cueCount: number;
  videoCount: number;
  commentCount: number;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const h: Record<string, string> = {};
  const token = getSessionToken();
  if (token) h.Authorization = `Bearer ${token}`;
  const resp = await fetch(apiBase() + path, { credentials: 'include', ...init, headers: h });
  let data: unknown = null;
  try { data = await resp.json(); } catch { /* 不是 JSON */ }
  if (!resp.ok) {
    const msg = (data as { error?: string } | null)?.error || `HTTP ${resp.status}`;
    const e = new Error(msg) as Error & { status: number };
    e.status = resp.status;
    throw e;
  }
  return data as T;
}

/** 這個專案刪掉、還在 30 天內的歌（最近刪的在前） */
export function listDeletedSongs(projectId: string): Promise<{ songs: DeletedSong[]; keepDays: number }> {
  return call(`/api/projects/${enc(projectId)}/songs/deleted`);
}

/** 救回來：cue、留言、影片版本、音樂、審片紀錄原封不動回來，歌單順序回到原本的位置 */
export function restoreDeletedSong(projectId: string, songId: string): Promise<{ ok: true; songId: string; name: string; code: string | null; order: number }> {
  return call(`/api/projects/${enc(projectId)}/songs/${enc(songId)}/restore`, { method: 'POST' });
}

// ─── 畫面上的字 ───

// 刪歌確認視窗的內文在 cueBadge.ts 的 deleteSongMessage（Cue 庫專案多一句）

/** 刪完了之後的小提示 */
export function deletedSongToast(name: string): string {
  return `已刪除「${name}」，${SONG_KEEP_DAYS} 天內可以到「最近刪除的歌」救回來`;
}

/** 歌單上的入口（沒有刪掉的歌就不顯示，見 showTrashEntry） */
export function trashEntryLabel(count: number): string {
  return `最近刪除的歌（${count}）`;
}

/** 入口要不要出現：只有製作、而且真的有刪掉的歌 */
export function showTrashEntry(isEditor: boolean, count: number): boolean {
  return isEditor && count > 0;
}

/** 「S02 副歌」（沒有代號就只有歌名） */
export function deletedSongTitle(s: Pick<DeletedSong, 'code' | 'name'>): string {
  return [s.code, s.name].filter(Boolean).join(' ');
}

/** 「阿宏 刪的 · 11/02 14:30」 */
export function deletedSongMeta(s: Pick<DeletedSong, 'deletedBy' | 'deletedAt'>): string {
  const who = s.deletedBy?.name ? `${s.deletedBy.name} 刪的` : '刪掉的';
  return [who, formatDateTime(s.deletedAt)].filter(Boolean).join(' · ');
}

/** 「還剩 12 天」 / 「今天就會清掉」 */
export function daysLeftText(daysLeft: number): string {
  return daysLeft > 0 ? `還剩 ${daysLeft} 天` : '今天就會清掉';
}

/** 還剩 3 天以內用警告色 */
export function isSoon(daysLeft: number): boolean {
  return daysLeft <= 3;
}

/** 「2 個 cue、1 版影片、3 則留言」（沒有的不寫；都沒有 = 空字串） */
export function deletedSongInside(s: Pick<DeletedSong, 'cueCount' | 'videoCount' | 'commentCount'>): string {
  return [
    s.cueCount > 0 ? `${s.cueCount} 個 cue` : '',
    s.videoCount > 0 ? `${s.videoCount} 版影片` : '',
    s.commentCount > 0 ? `${s.commentCount} 則留言` : '',
  ].filter(Boolean).join('、');
}

/** 救回來成功的小提示 */
export function restoredSongToast(s: Pick<DeletedSong, 'code' | 'name'>): string {
  return `已救回「${deletedSongTitle(s)}」，回到歌單原本的位置`;
}

/** 救回來失敗：後台寫好了白話（410 超過 30 天、409 剛剛被救回來 / 清掉了），直接用 */
export function restoreErrorText(e: unknown): string {
  const m = e instanceof Error ? e.message : String(e);
  return '救不回來：' + m;
}
